import type { RefObjectForHighchartsReact } from "components/charts/modern/abstract/HighchartsChart";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import handleClickInLegend from "components/charts/modern/helpers/handleClickInLegend";
import getColorThemeForIndex from "components/charts/modern/quotes/getColorThemeForIndex";
import PercentageSeriesTooltip from "components/charts/modern/tooltip/PercentageSeriesTooltip";
import useBrazilianRealEstateFundIssuer from "components/company/v3/context/hooks/useBrazilianRealEstateFundIssuer";
import SadFaceIcon from "components/icons/SadFaceIcon";
import classNames from "components/ui/classNames";
import { useMinWidthTailwindBreakpoints } from "components/ui/hooks/useTailwindBreakpoints";
import useTheme from "components/ui/useTheme";
import aeonik from "components/utils/aeonik";
import { useRef } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { FormattedMessage, useIntl } from "react-intl";
import colors from "tailwindcss/colors";

export default function FiisDistributionOfAssetsContent() {
  const intl = useIntl();
  const theme = useTheme();
  const chartRef = useRef<RefObjectForHighchartsReact | null>(null);

  const { distributionOfAssets } = useBrazilianRealEstateFundIssuer();
  const breakpoints = useMinWidthTailwindBreakpoints();

  if (!distributionOfAssets) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600 dark:text-slate-400">
          <div>
            <SadFaceIcon />
          </div>

          <p>
            <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
          </p>
        </div>
      </div>
    );
  }

  const assets = [
    {
      label: intl.formatMessage({ defaultMessage: "Caixa", id: "aXWb4c" }),
      value: Math.round(distributionOfAssets.cash * 100),
    },
    {
      label: intl.formatMessage({ defaultMessage: "Imóveis", id: "uX6eHh" }),
      value: Math.round(distributionOfAssets.realEstate * 100),
    },
    {
      label: intl.formatMessage({ defaultMessage: "Fundos", id: "Nk+yFx" }),
      value: Math.round(distributionOfAssets.fundsOfFunds * 100),
    },
    {
      label: intl.formatMessage({ defaultMessage: "Papéis", id: "NliJqs" }),
      value: Math.round(distributionOfAssets.otherInvestments * 100),
    },
  ]
    .filter((node) => node.value > 0)
    .sort((a, b) => b.value - a.value);

  const legendItemHoverColor = theme === "dark" ? colors.slate[400] : colors.slate[500];
  const legendItemColor = theme === "dark" ? colors.slate[300] : colors.slate[800];
  const plotBorderColor = theme === "dark" ? colors.slate[700] : colors.slate[100];
  const tooltipBgColor = theme === "dark" ? colors.slate[800] : colors.white;
  const tooltipBorderColor = theme === "dark" ? colors.slate[950] : colors.slate[200];
  const dataLabelColor = theme === "dark" ? colors.slate[300] : colors.slate[800];

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      type: "pie",
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 0,
      plotBorderColor,
    },
    scrollbar: {
      enabled: false,
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    legend: {
      align: "left",
      enabled: true,
      padding: 4,
      alignColumns: false,
      itemDistance: 16,
      layout: "horizontal",
      itemStyle: {
        color: legendItemColor,
        fontFamily: aeonik.style.fontFamily,
        fontWeight: "500",
        fontSize: "14px",
        textOverflow: "ellipsis",
        width: 240,
        textOutline: "none",
      },
      itemHoverStyle: {
        color: legendItemHoverColor,
      },
    },
    series: [
      {
        type: "pie",
        size: "70%",
        innerSize: "50%",
        borderRadius: 4,
        showInLegend: true,
        data: assets.map((node, index) => {
          const colorTheme = getColorThemeForIndex(index, theme);

          return {
            name: node.label,
            y: node.value,
            color: colorTheme.color,
            borderColor: colorTheme.lineColor,
            borderWidth: 1.5,
            custom: node,
            dataLabels: {
              enabled: breakpoints.lg || breakpoints.xl,
              style: {
                color: dataLabelColor,
                fontFamily: aeonik.style.fontFamily,
                fontWeight: "500",
                fontSize: "13px",
                textOutline: "none",
                textOverflow: "ellipsis",
                width: 240,
              },
            },
          };
        }),
      },
    ],
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: theme === "dark" ? "rgba(0, 0, 0, 0.3)" : "rgba(0, 0, 0, 0.1)",
      },
      backgroundColor: tooltipBgColor,
      borderColor: tooltipBorderColor,
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        return renderToStaticMarkup(<PercentageSeriesTooltip context={this} />);
      },
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
        events: {
          legendItemClick(event) {
            handleClickInLegend(event, this, chartRef);
          },
        },
      },
    },
    responsive: {
      rules: [
        {
          condition: {
            callback: () => {
              return breakpoints.lg || breakpoints.xl;
            },
          },
          chartOptions: {
            legend: {
              enabled: false,
            },
          },
        },
      ],
    },
  };

  return (
    <div className="grid h-auto w-full grid-cols-1 items-start gap-4 lg:grid-cols-[max-content,1fr] lg:gap-8">
      <div className="flex h-full flex-col space-y-4 sm:w-96 sm:max-w-md">
        <div className="flex h-full w-full flex-col space-y-4">
          <div className="h-full rounded-2xl border-none border-slate-200 bg-slate-50/80 p-4 dark:border-slate-700 dark:bg-slate-800/80">
            <ul className="divide-y divide-gray-100 dark:divide-slate-700">
              {assets.map(({ label, value }, index) => {
                const colorTheme = getColorThemeForIndex(index, theme);

                return (
                  <li
                    key={label}
                    className={classNames("flex items-center justify-between gap-x-6 py-4", "first:pt-0 last:pb-0")}
                  >
                    <p className="text-sm font-bold text-gray-700 dark:text-slate-300 lg:line-clamp-1">{label}</p>

                    <div className="pl-8">
                      <div className="flex items-center space-x-4">
                        <div>
                          <span className="font-mono text-xs font-semibold text-slate-700 dark:text-slate-300">
                            {value}%
                          </span>
                        </div>

                        <span
                          aria-hidden={true}
                          className="relative inline-flex h-2.5 w-2.5 items-center justify-center rounded-full"
                          style={{ backgroundColor: colorTheme.lineColor }}
                        />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>

      <div className="flex h-full items-center rounded-xl bg-slate-50/50 dark:bg-slate-800/50">
        <HighchartsChart options={options} className="h-full w-full" />
      </div>
    </div>
  );
}
